import React, { useCallback, useEffect, useState } from "react";
import api from "../../lib/api";
import UserAccessTable from "../UserAccessTable";
import { Users } from "@phosphor-icons/react";

/**
 * Admin → Customer Profile → Portal Users
 * ---------------------------------------
 * Full RBAC-scoped admin view of customer_users for one customer.
 * Staff/admin actors can add, edit, disable, remove, and regenerate
 * activation links regardless of the last-active-customer_admin
 * guard (the customer-portal add-user surface has the guard for
 * customer-self-service).
 */
export default function PortalUsersSection({ customerId, customerName }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  // Split loaders so `refresh` (used by <UserAccessTable/>) doesn't
  // unmount the table — that would kill the Activation Link Modal that
  // sits inside it.
  const refresh = useCallback(async () => {
    if (!customerId) return;
    setErr(null);
    try {
      const { data } = await api.get(`/admin/customers/${customerId}/portal-users`);
      setUsers(data.users || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not load portal users.");
    }
  }, [customerId]);

  const initialLoad = useCallback(async () => {
    setLoading(true);
    try { await refresh(); } finally { setLoading(false); }
  }, [refresh]);

  useEffect(() => { initialLoad(); }, [initialLoad]);

  return (
    <section className="mt-8 pt-8 border-t border-slate-200" data-testid="admin-portal-users-section">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-9 h-9 rounded-lg bg-indigo-50 text-indigo-700 grid place-items-center">
          <Users size={18} weight="bold" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Portal Users</h2>
          <p className="text-xs text-slate-500">
            People who can sign into the customer portal for{" "}
            <span className="font-medium">{customerName || customerId}</span>.
          </p>
        </div>
      </div>
      {err && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800 mb-3">
          {err}
        </div>
      )}
      {loading ? (
        <div className="text-sm text-slate-500" data-testid="admin-portal-users-loading">Loading…</div>
      ) : (
        <UserAccessTable
          api={api}
          basePath="/admin/portal-users"
          addPath={`/admin/customers/${customerId}/portal-users`}
          canManage
          title=""
          subtitle=""
          users={users}
          refresh={refresh}
          testIdPrefix="admin-portal-users"
          showAdminSecurityControls
        />
      )}
    </section>
  );
}
